// Where board columns live (Prospect "prospect", Pipeline Board "loans").
//
//   Account  GSWebAPIv2 api/PipelineBoard/Boards + SaveBoard (user_settings.swiftBoardColumns), so columns follow
//            the person to any computer. This is the source of truth once the API is deployed.
//   Browser  localStorage (boardColumns.js). A copy of the account columns, and the fallback while the API
//            isn't deployed (404) or can't be reached.
//
// Rules, in order (debug note: each result says which one applied in `where` / `note`):
//   1. The account has this board            → use it (and refresh the browser copy).
//   2. The account doesn't, browser does     → use the browser's and upload it once, so nobody loses the
//                                              columns they built before account saving existed.
//   3. Neither                               → columns: null; the dashboard uses its defaults and saves them.
//   4. The API fails                         → the browser's copy, with a note (no note for 404: not deployed).
// While viewing as someone ("view as"): their account columns or the defaults, never your own browser copy;
// changes stay on screen and are not saved anywhere (the server refuses SaveBoard while impersonating too).
import api from '@/api/connect.service.js'
import { loadBoardColumns, saveBoardColumns, cleanColumn } from '@/helpers/connect/boardColumns.js'

const TTL_MS = 60 * 1000
let cache = null // { who, at, promise } — both dashboards share one Boards call
const chains = {} // board → promise; saves go out one at a time, each sending the newest columns
const latest = {}

function fetchBoards() {
  const who = api.viewingAs()
  if (cache && cache.who === who && Date.now() - cache.at < TTL_MS) return cache.promise
  const promise = api.getBoards()
  cache = { who, at: Date.now(), promise }
  promise.catch(() => { if (cache && cache.promise === promise) cache = null })
  return promise
}

export const VIEWING_AS_NOTE = "You're viewing as someone else: you see their columns, and changes you make here aren't saved."

/** → { columns: [{ id, title, filters }] | null, where: 'account' | 'browser' | 'none', viewingAs, note } */
export async function loadBoard(board) {
  const viewingAs = !!api.viewingAs()
  let server
  try {
    server = await fetchBoards()
  } catch (e) {
    const notDeployed = e && e.status === 404
    if (viewingAs) return { columns: null, where: 'none', viewingAs, note: VIEWING_AS_NOTE }
    return {
      columns: loadBoardColumns(board),
      where: 'browser',
      viewingAs,
      note: notDeployed ? '' : `Couldn't reach your saved columns (${e.message}), so these are this browser's.`
    }
  }
  const onAccount = server && Array.isArray(server[board]) ? server[board].map(cleanColumn) : null
  if (onAccount) {
    if (!viewingAs) saveBoardColumns(board, onAccount)
    return { columns: onAccount, where: 'account', viewingAs, note: viewingAs ? VIEWING_AS_NOTE : '' }
  }
  if (viewingAs) return { columns: null, where: 'none', viewingAs, note: VIEWING_AS_NOTE }
  const local = loadBoardColumns(board)
  if (!local) return { columns: null, where: 'account', viewingAs, note: '' }
  const up = await saveBoard(board, local)
  return { columns: local, where: up.where, viewingAs, note: up.note }
}

/** → { saved: boolean, where: 'account' | 'browser' | 'none', note } */
export function saveBoard(board, columns) {
  const defs = (columns || []).map(cleanColumn)
  if (api.viewingAs()) return Promise.resolve({ saved: false, where: 'none', note: VIEWING_AS_NOTE })
  saveBoardColumns(board, defs)
  latest[board] = defs
  const run = (chains[board] || Promise.resolve()).then(async () => {
    try {
      await api.saveBoard(board, latest[board])
      cache = null
      return { saved: true, where: 'account', note: '' }
    } catch (e) {
      return { saved: false, where: 'browser', note: e && e.status === 404 ? '' : `Saved in this browser only: ${e.message}` }
    }
  })
  chains[board] = run
  return run
}

/** The footer line under a board. */
export function whereLabel(where) {
  if (where === 'account') return 'Columns are saved to your account.'
  if (where === 'browser') return 'Columns are kept in this browser.'
  return ''
}
